import React, { useState, useEffect, useRef } from 'react';
import { MdNotifications, MdNotificationsActive, MdClose, MdAccessTime, MdPerson, MdLocationOn } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useReminders } from '../hooks/useReminders';
import { useAuth } from '../hooks/useAuth';
import { notificationPath } from '../notificationLinks';
import API from '../api';
import { useLanguage } from '../i18n';

function decodeVapidKey(value) {
    const padding = '='.repeat((4 - value.length % 4) % 4);
    const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
    return Uint8Array.from(atob(base64), character => character.charCodeAt(0));
}

function minuteLabel(min, t) {
    if (min <= 0) return t('reminder.endingNow');
    if (min === 1) return t('reminder.oneMinute');
    return `${min} ${t('reminder.minutes')}`;
}

function urgencyStyle(min) {
    if (min <= 5) return { bg: '#fee2e2', border: '#fca5a5', color: '#b91c1c', dot: '#ef4444' };
    if (min <= 15) return { bg: '#fff7ed', border: '#fdba74', color: '#c2410c', dot: '#f97316' };
    return { bg: '#fef9c3', border: '#fcd34d', color: '#854d0e', dot: '#f59e0b' };
}

export default function ReminderBell() {
    const { t } = useLanguage();
    const { reminders, renewals, notifications, newCount, clearNew } = useReminders();
    const navigate = useNavigate();
    const { isAdmin, isManager } = useAuth();
    const staff = isAdmin || isManager;
    const [open, setOpen] = useState(false);
    const [played, setPlayed] = useState(new Set());
    const [notificationPermission, setNotificationPermission] = useState(
        typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
    );
    const panelRef = useRef(null);

    // Native alerts are available after the user explicitly grants permission.
    useEffect(() => {
        if (newCount > 0 && notificationPermission === 'granted') {
            const freshReminders = reminders.filter(r => !played.has(`booking-${r.id}`));
            const freshRenewals = renewals.filter(r => !played.has(`renewal-${r.id}`));
            const freshNotifications = notifications.filter(item => !played.has(`notification-${item.id}`));
            freshReminders.forEach(r => {
                new Notification(t('reminder.bookingEnding'), {
                    body: `${r.space_name} - ${r.guest_name || r.booked_by_name || t('reminder.guest')}: ${minuteLabel(r.minutes_remaining, t)}`,
                    icon: '/favicon.ico', tag: `booking-${r.id}`,
                });
            });
            freshRenewals.forEach(r => {
                new Notification(t('reminder.rentalRenewal'), {
                    body: `${r.space_name} ${t('reminder.renewalIn')} ${r.days_remaining} ${t('reminder.days')}.`,
                    icon: '/favicon.ico', tag: `renewal-${r.id}`,
                });
            });
            freshNotifications.forEach(item => {
                new Notification(item.title, {
                    body: item.message,
                    icon: '/favicon.ico', tag: `notification-${item.id}`,
                });
            });
            if (freshReminders.length || freshRenewals.length || freshNotifications.length) {
                setPlayed(previous => new Set([
                    ...previous,
                    ...freshReminders.map(r => `booking-${r.id}`),
                    ...freshRenewals.map(r => `renewal-${r.id}`),
                    ...freshNotifications.map(item => `notification-${item.id}`),
                ]));
            }
        }
    }, [newCount, notificationPermission, notifications, played, reminders, renewals, t]);

    // Close panel when clicking outside
    useEffect(() => {
        function handleClick(e) {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        if (open) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    useEffect(() => {
        const openFromTile = () => { setOpen(true); clearNew(); API.post('/notifications/read').catch(() => {}); };
        window.addEventListener('fms:open-alerts', openFromTile);
        return () => window.removeEventListener('fms:open-alerts', openFromTile);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpen = async () => {
        const nextOpen = !open;
        setOpen(nextOpen);
        if (nextOpen) {
            clearNew();
            try { await API.post('/notifications/read'); } catch (error) { console.error('Could not mark notifications as read', error); }
        }
    };

    const enableNotifications = async () => {
        if (typeof Notification === 'undefined') return;
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);
        if (permission !== 'granted' || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
        try {
            const registration = await navigator.serviceWorker.ready;
            const { data } = await API.get('/notifications/push/public-key');
            if (!data.public_key) return;
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: decodeVapidKey(data.public_key)
            });
            await API.post('/notifications/push/subscribe', subscription.toJSON());
        } catch (error) {
            console.error('Push notification setup failed:', error);
        }
    };

    return (
        <div style={{ position: 'relative' }} ref={panelRef}>
            {/* Bell button */}
            <button
                onClick={handleOpen}
                style={{
                    position: 'relative',
                    border: '1px solid #e2e8f0',
                    cursor: 'pointer',
                    padding: '7px 10px',
                    borderRadius: 8,
                    background: reminders.length > 0 || notifications.some(item => !item.is_read) ? '#fef3c7' : '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 38,
                    minHeight: 38,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    transition: 'all 0.2s'
                }}
                title={newCount > 0 ? `${newCount} ${t('reminder.new')}` : t('reminder.title')}
            >
                <MdNotifications size={22} style={{ color: reminders.length > 0 || notifications.some(item => !item.is_read) ? '#b45309' : '#475569' }} />
                {newCount > 0 && (
                    <span style={{
                        position: 'absolute', top: 2, right: 2,
                        width: 16, height: 16, borderRadius: '50%',
                        background: '#ef4444', color: '#fff',
                        fontSize: 10, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '2px solid #fff'
                    }}>{newCount > 9 ? '9+' : newCount}</span>
                )}
                {/* Pulse animation for urgent reminders */}
                {reminders.some(r => r.minutes_remaining <= 10) && (
                    <span style={{
                        position: 'absolute', top: 2, right: 2, width: 16, height: 16,
                        borderRadius: '50%', background: 'rgba(239,68,68,0.3)',
                        animation: 'pulse 1.5s infinite',
                    }} />
                )}
            </button>

            {/* Dropdown panel */}
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 8,
                    width: 'min(360px, calc(100vw - 24px))', maxHeight: 480, overflowY: 'auto',
                    background: '#fff', borderRadius: 14, zIndex: 5000,
                    boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
                    border: '1px solid #e2e8f0'
                }}>
                    {/* Header */}
                    <div style={{
                        padding: '14px 16px', borderBottom: '1px solid #f1f5f9',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: '#fffbeb', borderRadius: '14px 14px 0 0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <MdAccessTime style={{ color: '#b45309', fontSize: 18 }} />
                            <span style={{ fontWeight: 700, fontSize: 14, color: '#374151' }}>
                                {t('reminder.title')}
                            </span>
                            {newCount > 0 && (
                                <span style={{ background: '#f59e0b', color: '#fff', fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10 }}>
                                    {newCount > 9 ? '9+' : newCount}
                                </span>
                            )}
                        </div>
                        <button onClick={() => setOpen(false)}
                            style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 }}>
                            <MdClose size={16} />
                        </button>
                    </div>

                    {notificationPermission === 'default' && (
                        <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', background: '#eff6ff', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <MdNotificationsActive size={20} style={{ color: '#0b5ed7', flexShrink: 0 }} />
                            <span style={{ flex: 1, fontSize: 12, color: '#1e3a5f' }}>{t('reminder.enableDescription')}</span>
                            <button type="button" className="btn btn-primary btn-sm" onClick={enableNotifications}>{t('reminder.enable')}</button>
                        </div>
                    )}
                    {notificationPermission === 'denied' && (
                        <div style={{ padding: '9px 14px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc', fontSize: 12, color: '#52657d' }}>
                            {t('reminder.blocked')}
                        </div>
                    )}

                    {/* Reminder items */}
                    {notifications.length === 0 && reminders.length === 0 && renewals.length === 0 ? (
                        <div style={{ padding: '32px 20px', textAlign: 'center', color: '#94a3b8' }}>
                            <MdNotifications size={36} style={{ color: '#d1d5db', marginBottom: 8 }} />
                            <div style={{ fontSize: 13 }}>{t('reminder.none')}</div>
                        </div>
                    ) : (
                        <div style={{ padding: '8px 0' }}>
                            {notifications.map(item => (
                                <div key={`notification-${item.id}`} role="button" tabIndex={0} onClick={() => { setOpen(false); navigate(notificationPath(item, staff)); }} onKeyDown={event => { if (event.key === 'Enter') { setOpen(false); navigate(notificationPath(item, staff)); } }} style={{ margin: '6px 10px', padding: '12px 14px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${item.is_read ? '#e2e8f0' : '#bfdbfe'}`, background: item.is_read ? '#fff' : '#eff6ff' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>{item.title}</span>
                                        <span style={{ flexShrink: 0, fontSize: 11, color: '#94a3b8' }}>{new Date(item.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                                    </div>
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#52657d' }}>{item.message}</div>
                                </div>
                            ))}
                            {renewals.map(r => <div key={`renewal-${r.id}`} style={{ margin: '6px 10px', padding: '12px 14px', borderRadius: 10, border: '1px solid #fdba74', background: '#fff7ed' }}><div style={{ fontWeight: 700, color: '#c2410c' }}>{t('reminder.renewalIn')} {r.days_remaining} {t('reminder.days')}</div><div style={{ fontSize: 13, marginTop: 4 }}>{r.space_name} · {r.building_name}</div><div style={{ fontSize: 12, color: '#64748b' }}>{r.guest_name || t('reminder.tenant')} · {r.guest_email || t('reminder.noEmail')}</div></div>)}
                            {reminders.map(r => {
                                const s = urgencyStyle(r.minutes_remaining);
                                return (
                                    <div key={r.id} style={{
                                        margin: '6px 10px', padding: '12px 14px',
                                        borderRadius: 10, border: `1px solid ${s.border}`,
                                        background: s.bg
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{
                                                    width: 8, height: 8, borderRadius: '50%',
                                                    background: s.dot, display: 'inline-block', flexShrink: 0
                                                }} />
                                                <span style={{ fontSize: 13, fontWeight: 700, color: s.color }}>
                                                    {minuteLabel(r.minutes_remaining, t)}
                                                </span>
                                            </div>
                                            <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                                {t('reminder.ends')} {new Date(r.end_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>
                                            <MdLocationOn size={14} style={{ color: '#64748b', flexShrink: 0 }} />
                                            {r.space_name}
                                            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>· {r.building_name}</span>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#64748b' }}>
                                            <MdPerson size={14} style={{ flexShrink: 0 }} />
                                            {r.guest_name || r.booked_by_name || t('reminder.guest')}
                                            {r.guest_phone && <span>· {r.guest_phone}</span>}
                                            {r.attendees > 1 && <span>· {r.attendees} {t('reminder.people')}</span>}
                                        </div>

                                        {r.title && (
                                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>📋 {r.title}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Footer note */}
                    <div style={{ padding: '10px 16px', borderTop: '1px solid #f1f5f9', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                        {t('reminder.refreshes')}
                    </div>
                </div>
            )}

            {/* Pulse keyframe via style tag */}
            <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(2); opacity: 0; }
        }
      `}</style>
        </div>
    );
}
